/**
 * Unified per-role **Accesso ai dati** grid (access-model A — "Access grid +
 * panel"). The roles × surfaces matrix IS the at-a-glance overview AND the
 * editor, so the two can't drift:
 *
 *  - **Tabelle**: each cell shows the role's effective access via the pure
 *    `summarizeTableAccess` (default-open A.0 — a faint "Tutto" recedes,
 *    exceptions tint and pop). Click a cell → the docked panel edits rows /
 *    fields / writes through the reusable `PolicyControls`, plus the
 *    **Assistente** inherit/off toggle.
 *  - **Viste**: inherit/off only (rows & columns follow the base table).
 *  - **Dataset esterni**: an explicit grant/deny allow-list — **foreign data
 *    only** (`binding !== internal`, no `app_table_id`); the app's OWN tables
 *    never reappear here (that was the confusing double-governance bug).
 *
 * Default-open: a surface with no exception shows full access; you configure
 * only the exception. The assistant column edits `app_roles.assistant_scope`
 * (ADR-0028 N1); what it reads comes from `effective_assistant_scope`.
 */
import * as React from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { BookOpen, Database, Eye, FileBarChart2, Filter, Lock, ShieldCheck, Table2, X } from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  appsApi,
  type AssistantScope,
  type ColumnOut,
  type DatasetGrant,
  type PolicyOut,
  type RoleOut,
} from "@/lib/api-apps";
import { PolicyControls } from "@/components/apps/policyControls";
import { DatasetPolicyControls } from "@/components/apps/datasetPolicyControls";
import { summarizeTableAccess, describeTableAccess, type CellSummary } from "@/components/apps/accessGrid";

type SurfaceKind = "table" | "view" | "dataset";
interface Selection {
  kind: SurfaceKind;
  id: string;
  roleId: string;
}

export function AccessMatrixSection({
  appId,
  canWrite,
  surface,
}: {
  appId: string;
  canWrite: boolean;
  /** ADR-0059 — render ONLY this surface's band (the "Accesso ai dati" sub-items
   *  in Membri e ruoli). Undefined = the full matrix (all four bands). */
  surface?: SurfaceKind | "kb" | "report";
}) {
  const { t } = useTranslation();
  const [sel, setSel] = React.useState<Selection | null>(null);
  const [onlyExceptions, setOnlyExceptions] = React.useState(false);
  const show = (s: SurfaceKind | "kb" | "report") => !surface || surface === s;

  const rolesQ = useQuery({ queryKey: ["apps", appId, "roles"], queryFn: () => appsApi.listRoles(appId) });
  const tablesQ = useQuery({ queryKey: ["apps", appId, "tables"], queryFn: () => appsApi.listTables(appId) });
  const viewsQ = useQuery({ queryKey: ["apps", appId, "datasets"], queryFn: () => appsApi.listDatasets(appId) });
  // ADR-0059 — the matrix scopes per-role visibility over the resources the APP
  // has LINKED (curated in Core → Dataset / Knowledge base), NOT the whole tenant
  // catalog. Linking is the outer boundary; the toggle here is the per-role grant.
  const datasetsQ = useQuery({
    queryKey: ["apps", appId, "linked-datasets"],
    queryFn: () => appsApi.listLinkedDatasets(appId),
  });
  const kbsQ = useQuery({
    queryKey: ["apps", appId, "linked-kbs"],
    queryFn: () => appsApi.listLinkedKbs(appId),
  });
  // ADR-0060 S5 — linked Report Types (the per-role render grant rides the same
  // assistant_scope blob: report_type_ids, an explicit allow-list like kb_ids).
  const reportTypesQ = useQuery({
    queryKey: ["apps", appId, "linked-report-types"],
    queryFn: () => appsApi.listLinkedReportTypes(appId),
  });
  const membersQ = useQuery({ queryKey: ["apps", appId, "members"], queryFn: () => appsApi.listMembers(appId) });

  const roles = rolesQ.data ?? [];
  const tables = React.useMemo(() => tablesQ.data ?? [], [tablesQ.data]);
  const views = viewsQ.data ?? [];
  // Already curated at the app level (Core → Dataset / Knowledge base): the link
  // step did the "external + published" filtering, so here they're used verbatim.
  const datasets = datasetsQ.data ?? [];
  const kbs = kbsQ.data ?? [];
  const reportTypes = reportTypesQ.data ?? [];

  const detailQs = useQueries({
    queries: tables.map((tb) => ({
      queryKey: ["apps", appId, "tables", tb.id],
      queryFn: () => appsApi.getTable(appId, tb.id),
    })),
  });
  const policyQs = useQueries({
    queries: tables.map((tb) => ({
      queryKey: ["apps", appId, "tables", tb.id, "policies"],
      queryFn: () => appsApi.listPolicies(appId, tb.id),
    })),
  });

  const memberCount = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const mem of membersQ.data ?? []) m.set(mem.role_id, (m.get(mem.role_id) ?? 0) + 1);
    return m;
  }, [membersQ.data]);

  const qc = useQueryClient();
  const saveScope = useMutation({
    mutationFn: ({ roleId, scope }: { roleId: string; scope: AssistantScope }) =>
      appsApi.updateRole(appId, roleId, { assistant_scope: scope }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "roles"] }),
  });

  // Recompute a role's scope deltas when an assistant toggle flips.
  function toggleAssistant(role: RoleOut, kind: SurfaceKind, id: string, on: boolean) {
    const s = role.assistant_scope ?? {};
    if (kind === "dataset") {
      const next = new Set(s.dataset_ids ?? []);
      if (on) next.add(id);
      else next.delete(id);
      saveScope.mutate({ roleId: role.id, scope: { ...s, dataset_ids: [...next] } });
    } else {
      const key = kind === "table" ? "off_table_ids" : "off_projection_ids";
      const next = new Set((s as Record<string, string[]>)[key] ?? []);
      // ON = inherit (remove the exclusion); OFF = exclude.
      if (on) next.delete(id);
      else next.add(id);
      saveScope.mutate({ roleId: role.id, scope: { ...s, [key]: [...next] } });
    }
  }

  const polFor = (tableIdx: number, roleId: string): PolicyOut | undefined =>
    policyQs[tableIdx]?.data?.find((p) => p.role_id === roleId);

  const tableSummary = (tableIdx: number, roleId: string): CellSummary =>
    summarizeTableAccess(polFor(tableIdx, roleId));

  // Assistant readability per role (effective set ∪ its own exclusions).
  function readable(role: RoleOut, kind: "table" | "view", id: string): boolean {
    const eff = role.effective_assistant_scope ?? {};
    const off = role.assistant_scope ?? {};
    if (kind === "table")
      return new Set([...(eff.app_table_ids ?? []), ...(off.off_table_ids ?? [])]).has(id);
    return new Set([...(eff.projection_ids ?? []), ...(off.off_projection_ids ?? [])]).has(id);
  }
  const assistantOn = (role: RoleOut, kind: "table" | "view", id: string): boolean => {
    const off = role.assistant_scope ?? {};
    const excluded = (kind === "table" ? off.off_table_ids : off.off_projection_ids) ?? [];
    return readable(role, kind, id) && !excluded.includes(id);
  };
  const datasetGranted = (role: RoleOut, id: string): boolean =>
    (role.assistant_scope?.dataset_ids ?? []).includes(id);
  // ADR-0035 RAG: a KB is an explicit allow-list (on/off), like external datasets.
  const kbGranted = (role: RoleOut, id: string): boolean =>
    (role.assistant_scope?.kb_ids ?? []).includes(id);
  function toggleKb(role: RoleOut, id: string, on: boolean) {
    const s = role.assistant_scope ?? {};
    const next = new Set(s.kb_ids ?? []);
    if (on) next.add(id);
    else next.delete(id);
    saveScope.mutate({ roleId: role.id, scope: { ...s, kb_ids: [...next] } });
  }
  // ADR-0060 S5 — a report grant is on/off per role (the render boundary).
  const reportGranted = (role: RoleOut, id: string): boolean =>
    (role.assistant_scope?.report_type_ids ?? []).includes(id);
  function toggleReport(role: RoleOut, id: string, on: boolean) {
    const s = role.assistant_scope ?? {};
    const next = new Set(s.report_type_ids ?? []);
    if (on) next.add(id);
    else next.delete(id);
    saveScope.mutate({ roleId: role.id, scope: { ...s, report_type_ids: [...next] } });
  }

  // N4 — persist a granted dataset's rows+fields refinement into the role's
  // assistant_scope.dataset_grants (a null grant drops the entry). Same single
  // saveScope mutation as every other assistant_scope edit.
  function saveDatasetGrant(role: RoleOut, datasetId: string, grant: DatasetGrant | null) {
    const s = role.assistant_scope ?? {};
    const next: Record<string, DatasetGrant> = { ...(s.dataset_grants ?? {}) };
    if (grant) next[datasetId] = grant;
    else delete next[datasetId];
    saveScope.mutate({ roleId: role.id, scope: { ...s, dataset_grants: next } });
  }

  // "Solo eccezioni": keep a surface only if some role isn't fully-open / granted.
  const tableHasException = (i: number) =>
    roles.some((r) => tableSummary(i, r.id).state !== "full");
  const datasetHasException = (id: string) => !roles.every((r) => datasetGranted(r, id));

  if (rolesQ.isLoading) return null;
  if (roles.length === 0) {
    return (
      <section className="space-y-3">
        <Header t={t} onlyExceptions={onlyExceptions} setOnlyExceptions={setOnlyExceptions} showToggle={false} />
        <p className="text-sm text-muted-foreground">
          {t("apps.policiesNoRoles", "Create app roles first (Members & roles).")}
        </p>
      </section>
    );
  }

  const visibleTables = tables.map((tb, i) => ({ tb, i })).filter(({ i }) => !onlyExceptions || tableHasException(i));
  const visibleDatasets = datasets.filter((d) => !onlyExceptions || datasetHasException(d.id));

  return (
    <section className="space-y-3">
      <Header
        t={t}
        onlyExceptions={onlyExceptions}
        setOnlyExceptions={setOnlyExceptions}
        showToggle={show("table") || show("dataset")}
      />
      <p className="text-xs text-muted-foreground">
        {t(
          "apps.accessMatrixHint",
          "What members of this role can see — rows, columns and writes per surface — and whether the voice/chat assistant inherits it. The assistant never sees more than the role does.",
        )}
      </p>

      <div className="overflow-x-auto rounded-lg border border-border/60">
        <Table>
          <Thead>
            <Tr>
              <Th className="w-48">{t("apps.surface", "Surface")}</Th>
              {roles.map((r) => (
                <Th key={r.id} className="text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="truncate">{r.name}</span>
                    <span className="rounded-md border border-border/60 px-1 text-[10px] text-muted-foreground">
                      {memberCount.get(r.id) ?? 0}
                    </span>
                  </div>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {show("table") && (<>
            <BandRow icon={Table2} label={t("apps.scopeTables", "Tables")} span={roles.length + 1} />
            {tables.length === 0 ? (
              <EmptyBand span={roles.length + 1} text={t("apps.scopeNoTables", "This app has no tables.")} />
            ) : (
              visibleTables.map(({ tb, i }) => (
                <Tr key={tb.id}>
                  <Td className="font-medium">{tb.name}</Td>
                  {roles.map((r) => (
                    <Td key={r.id} className="p-1.5">
                      <TableCellBtn
                        summary={tableSummary(i, r.id)}
                        assistant={assistantOn(r, "table", tb.id)}
                        selected={sel?.kind === "table" && sel.id === tb.id && sel.roleId === r.id}
                        onClick={() => setSel({ kind: "table", id: tb.id, roleId: r.id })}
                        t={t}
                      />
                    </Td>
                  ))}
                </Tr>
              ))
            )}
            </>)}

            {show("view") && (<>
            <BandRow icon={Eye} label={t("apps.scopeProjections", "Views")} span={roles.length + 1} />
            {views.length === 0 ? (
              <EmptyBand span={roles.length + 1} text={t("apps.scopeNoProjections", "This app has no views.")} />
            ) : (
              views.map((v) => (
                <Tr key={v.id}>
                  <Td className="font-medium">{v.name}</Td>
                  {roles.map((r) => (
                    <Td key={r.id} className="p-1.5">
                      <ViewCellBtn
                        readable={readable(r, "view", v.id)}
                        assistant={assistantOn(r, "view", v.id)}
                        selected={sel?.kind === "view" && sel.id === v.id && sel.roleId === r.id}
                        onClick={() => setSel({ kind: "view", id: v.id, roleId: r.id })}
                        t={t}
                      />
                    </Td>
                  ))}
                </Tr>
              ))
            )}
            </>)}

            {show("dataset") && (<>
            <BandRow icon={Database} label={t("apps.scopeDatasets", "Dataset")} span={roles.length + 1} />
            {datasets.length === 0 ? (
              <EmptyBand
                span={roles.length + 1}
                text={t("apps.scopeNoLinkedDatasets", "No linked dataset — add one in Core → Dataset.")}
              />
            ) : (
              visibleDatasets.map((d) => (
                <Tr key={d.id}>
                  <Td className="font-medium">{d.name}</Td>
                  {roles.map((r) => (
                    <Td key={r.id} className="p-1.5">
                      <DatasetCellBtn
                        granted={datasetGranted(r, d.id)}
                        disabled={!canWrite || saveScope.isPending}
                        onToggle={(on) => toggleAssistant(r, "dataset", d.id, on)}
                        selected={sel?.kind === "dataset" && sel.id === d.id && sel.roleId === r.id}
                        onEdit={() => setSel({ kind: "dataset", id: d.id, roleId: r.id })}
                        t={t}
                      />
                    </Td>
                  ))}
                </Tr>
              ))
            )}
            </>)}

            {show("kb") && (<>
            <BandRow icon={BookOpen} label={t("apps.scopeKnowledgeBases", "Knowledge bases")} span={roles.length + 1} />
            {kbs.length === 0 ? (
              <EmptyBand
                span={roles.length + 1}
                text={t("apps.scopeNoLinkedKbs", "No linked knowledge base — add one in Core → Knowledge base.")}
              />
            ) : (
              kbs.map((k) => (
                <Tr key={k.id}>
                  <Td className="font-medium">{k.name}</Td>
                  {roles.map((r) => (
                    <Td key={r.id} className="p-1.5">
                      <KbCellBtn
                        granted={kbGranted(r, k.id)}
                        disabled={!canWrite || saveScope.isPending}
                        onToggle={(on) => toggleKb(r, k.id, on)}
                        t={t}
                      />
                    </Td>
                  ))}
                </Tr>
              ))
            )}
            </>)}

            {show("report") && (<>
            <BandRow icon={FileBarChart2} label={t("apps.scopeReports", "Report")} span={roles.length + 1} />
            {reportTypes.length === 0 ? (
              <EmptyBand
                span={roles.length + 1}
                text={t("apps.scopeNoLinkedReports", "Nessun tipo di report collegato — aggiungilo in Core → Report.")}
              />
            ) : (
              reportTypes.map((rt) => (
                <Tr key={rt.id}>
                  <Td className="font-medium">{rt.name}</Td>
                  {roles.map((r) => (
                    <Td key={r.id} className="p-1.5">
                      <KbCellBtn
                        granted={reportGranted(r, rt.id)}
                        disabled={!canWrite || saveScope.isPending}
                        onToggle={(on) => toggleReport(r, rt.id, on)}
                        t={t}
                      />
                    </Td>
                  ))}
                </Tr>
              ))
            )}
            </>)}
          </Tbody>
        </Table>
      </div>

      <Legend t={t} />

      {sel && (
        <DockedPanel
          appId={appId}
          canWrite={canWrite}
          sel={sel}
          role={roles.find((r) => r.id === sel.roleId)!}
          tables={tables}
          views={views}
          datasets={datasets}
          columns={
            sel.kind === "table"
              ? (detailQs[tables.findIndex((tb) => tb.id === sel.id)]?.data?.columns ?? [])
              : []
          }
          policy={
            sel.kind === "table" ? polFor(tables.findIndex((tb) => tb.id === sel.id), sel.roleId) : undefined
          }
          onClose={() => setSel(null)}
          onToggleAssistant={toggleAssistant}
          onSaveDatasetGrant={saveDatasetGrant}
          assistantBusy={saveScope.isPending}
          assistantOn={assistantOn}
          datasetGranted={datasetGranted}
          readable={readable}
          t={t}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Shell pieces
// ---------------------------------------------------------------------------

function Header({
  t, onlyExceptions, setOnlyExceptions, showToggle,
}: {
  t: TFunction;
  onlyExceptions: boolean;
  setOnlyExceptions: (v: boolean) => void;
  showToggle: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
        {t("apps.accessMatrix", "Data access")}
      </h3>
      {showToggle && (
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" strokeWidth={1.5} />
          {t("apps.onlyExceptions", "Only exceptions")}
          <Switch checked={onlyExceptions} onCheckedChange={setOnlyExceptions} />
        </label>
      )}
    </div>
  );
}

function BandRow({ icon: Icon, label, span }: { icon: typeof Table2; label: string; span: number }) {
  return (
    <Tr>
      <Td colSpan={span} className="bg-surface/60 py-1.5 text-[11px] font-medium text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
          {label}
        </span>
      </Td>
    </Tr>
  );
}

function EmptyBand({ span, text }: { span: number; text: string }) {
  return (
    <Tr>
      <Td colSpan={span} className="text-xs text-muted-foreground">{text}</Td>
    </Tr>
  );
}

const cellWrap = (selected: boolean) =>
  "flex w-full items-center justify-between gap-1.5 rounded-md px-2 py-1.5 text-left transition " +
  (selected ? "border-2 border-info" : "border border-border/60 hover:bg-surface");

function AssistantDot({ on }: { on: boolean }) {
  return (
    <span
      className={
        "h-1.5 w-1.5 shrink-0 rounded-full " +
        (on ? "bg-muted-foreground" : "border border-border")
      }
    />
  );
}

function TableCellBtn({
  summary, assistant, selected, onClick, t,
}: {
  summary: CellSummary; assistant: boolean; selected: boolean; onClick: () => void;
  t: TFunction;
}) {
  return (
    <button type="button" onClick={onClick} className={cellWrap(selected)} title={describeTableAccess(summary)}>
      {summary.state === "none" ? (
        <Pill tone="muted-dashed">{t("apps.cellNone", "No access")}</Pill>
      ) : summary.state === "full" ? (
        <Pill tone="faint">{t("apps.cellFull", "Full")}</Pill>
      ) : (
        <span className="flex items-center gap-1">
          {summary.rows && (
            <Filter className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} aria-label={t("apps.cellRows", "rows filtered")} />
          )}
          {summary.hiddenCount > 0 && (
            <Pill tone="warn">{summary.hiddenCount} {t("apps.cellHidden", "hidden")}</Pill>
          )}
          {summary.writeLimited && !summary.rows && summary.hiddenCount === 0 && (
            <Pill tone="faint">{t("apps.cellWrites", "writes")}</Pill>
          )}
        </span>
      )}
      <AssistantDot on={assistant} />
    </button>
  );
}

function ViewCellBtn({
  readable, assistant, selected, onClick, t,
}: {
  readable: boolean; assistant: boolean; selected: boolean; onClick: () => void;
  t: TFunction;
}) {
  return (
    <button type="button" onClick={onClick} className={cellWrap(selected)}>
      <Pill tone="faint">
        <Lock className="mr-1 inline h-3 w-3" strokeWidth={1.5} />
        {t("apps.cellInherit", "Inherits")}
      </Pill>
      {readable ? <AssistantDot on={assistant} /> : <span className="text-[10px] text-muted-foreground">—</span>}
    </button>
  );
}

// A dataset cell is a DIRECT on/off toggle (off-default, ADR-0059) — click flips
// membership in the role's `assistant_scope.dataset_ids` allow-list, exactly like
// a KB cell. The `onEdit` (rows/fields refinement, N4) opens the docked panel and
// is offered ONLY once granted, as a secondary affordance — so the primary click
// TOGGLES (the fix for "the selector does nothing") instead of opening a panel.
function DatasetCellBtn({
  granted, disabled, selected, onToggle, onEdit, t,
}: {
  granted: boolean; disabled: boolean; selected: boolean;
  onToggle: (on: boolean) => void; onEdit: () => void;
  t: TFunction;
}) {
  return (
    <div
      className={
        cellWrap(selected) + (disabled ? " opacity-50" : "") + " !p-0 !border-0 gap-0"
      }
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => onToggle(!granted)}
        className={cellWrap(false) + " flex-1"}
      >
        {granted ? (
          <Pill tone="faint">{t("apps.cellGranted", "Granted")}</Pill>
        ) : (
          <Pill tone="muted-dashed">{t("apps.cellOff", "Off")}</Pill>
        )}
        {granted && <AssistantDot on />}
      </button>
      {granted && (
        <button
          type="button"
          aria-label={t("apps.datasetEditGrant", "Edit rows & fields")}
          onClick={onEdit}
          className={
            "ml-1 rounded-md border p-1.5 text-muted-foreground transition hover:bg-surface hover:text-foreground " +
            (selected ? "border-info" : "border-border/60")
          }
        >
          <Filter className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

// A KB cell is a DIRECT on/off toggle (no rows/fields refinement — ADR-0035): click flips
// membership in the role's `assistant_scope.kb_ids` allow-list.
function KbCellBtn({
  granted, disabled, onToggle, t,
}: {
  granted: boolean; disabled: boolean; onToggle: (on: boolean) => void;
  t: TFunction;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onToggle(!granted)}
      className={cellWrap(false) + " disabled:opacity-50"}
    >
      {granted ? (
        <Pill tone="faint">{t("apps.cellGranted", "Granted")}</Pill>
      ) : (
        <Pill tone="muted-dashed">{t("apps.cellOff", "Off")}</Pill>
      )}
      {granted && <AssistantDot on />}
    </button>
  );
}

function Pill({ tone, children }: { tone: "faint" | "warn" | "muted-dashed"; children: React.ReactNode }) {
  const cls =
    tone === "warn"
      ? "bg-warning/15 text-warning"
      : tone === "muted-dashed"
        ? "border border-dashed border-border text-muted-foreground"
        : "bg-surface text-muted-foreground";
  return (
    <span className={"inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] " + cls}>{children}</span>
  );
}

function Legend({ t }: { t: TFunction }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-[11px] text-muted-foreground">
      <span><Pill tone="faint">{t("apps.cellFull", "Full")}</Pill> {t("apps.legendFull", "full access")}</span>
      <span><Pill tone="warn">{t("apps.cellHidden", "hidden")}</Pill> {t("apps.legendHidden", "fields hidden")}</span>
      <span><Filter className="inline h-3 w-3" strokeWidth={1.5} /> {t("apps.legendRows", "rows filtered")}</span>
      <span><Lock className="inline h-3 w-3" strokeWidth={1.5} /> {t("apps.legendInherit", "inherits the base table")}</span>
      <span><AssistantDot on /> {t("apps.legendAssistant", "the assistant inherits")}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Docked panel — tier-aware editor for the selected cell
// ---------------------------------------------------------------------------

function DockedPanel({
  appId, canWrite, sel, role, tables, views, datasets, columns, policy,
  onClose, onToggleAssistant, onSaveDatasetGrant, assistantBusy, assistantOn, datasetGranted, readable, t,
}: {
  appId: string;
  canWrite: boolean;
  sel: Selection;
  role: RoleOut;
  tables: { id: string; name: string }[];
  views: { id: string; name: string }[];
  datasets: { id: string; name: string }[];
  columns: ColumnOut[];
  policy: PolicyOut | undefined;
  onClose: () => void;
  onToggleAssistant: (role: RoleOut, kind: SurfaceKind, id: string, on: boolean) => void;
  onSaveDatasetGrant: (role: RoleOut, datasetId: string, grant: DatasetGrant | null) => void;
  assistantBusy: boolean;
  assistantOn: (role: RoleOut, kind: "table" | "view", id: string) => boolean;
  datasetGranted: (role: RoleOut, id: string) => boolean;
  readable: (role: RoleOut, kind: "table" | "view", id: string) => boolean;
  t: TFunction;
}) {
  const name =
    sel.kind === "table"
      ? tables.find((x) => x.id === sel.id)?.name
      : sel.kind === "view"
        ? views.find((x) => x.id === sel.id)?.name
        : datasets.find((x) => x.id === sel.id)?.name;

  return (
    <div className="rounded-lg border border-border/60 bg-elevated/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium">
          {name} <span className="text-muted-foreground">· {role.name}</span>
        </p>
        <button type="button" aria-label={t("common.close", "Close")} onClick={onClose}>
          <X className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
        </button>
      </div>

      {sel.kind === "table" && (
        <div className="space-y-2">
          <div className="overflow-hidden rounded-md border border-border/60">
            <Table>
              <Tbody>
                <PolicyControls
                  appId={appId}
                  tableId={sel.id}
                  roleId={sel.roleId}
                  label={t("apps.access", "Access")}
                  policy={policy}
                  columns={columns}
                  disabled={!canWrite}
                />
              </Tbody>
            </Table>
          </div>
          <AssistantRow
            on={assistantOn(role, "table", sel.id)}
            readable={readable(role, "table", sel.id)}
            disabled={!canWrite || assistantBusy}
            onChange={(v) => onToggleAssistant(role, "table", sel.id, v)}
            t={t}
          />
        </div>
      )}

      {sel.kind === "view" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {t("apps.viewInheritsBase", "rows & columns inherited from the base table")}
          </p>
          <AssistantRow
            on={assistantOn(role, "view", sel.id)}
            readable={readable(role, "view", sel.id)}
            disabled={!canWrite || assistantBusy}
            onChange={(v) => onToggleAssistant(role, "view", sel.id, v)}
            t={t}
          />
        </div>
      )}

      {sel.kind === "dataset" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">{t("apps.datasetGrant", "Grant this external dataset to the role")}</span>
            <Switch
              checked={datasetGranted(role, sel.id)}
              disabled={!canWrite || assistantBusy}
              onCheckedChange={(v) => onToggleAssistant(role, "dataset", sel.id, v)}
            />
          </div>
          {datasetGranted(role, sel.id) ? (
            <DatasetPolicyControls
              appId={appId}
              datasetId={sel.id}
              grant={role.assistant_scope?.dataset_grants?.[sel.id]}
              disabled={!canWrite || assistantBusy}
              onSave={(g) => onSaveDatasetGrant(role, sel.id, g)}
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              {t("apps.datasetAllowlistHint", "Catalog datasets have no per-role RLS to inherit — grant them explicitly.")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AssistantRow({
  on, readable, disabled, onChange, t,
}: {
  on: boolean; readable: boolean; disabled: boolean; onChange: (v: boolean) => void;
  t: TFunction;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-2">
      <span className="text-sm">{t("apps.assistantInherits", "The assistant inherits this surface")}</span>
      {readable ? (
        <Switch checked={on} disabled={disabled} onCheckedChange={onChange} />
      ) : (
        <span className="text-[11px] text-muted-foreground">{t("apps.notReadable", "not readable")}</span>
      )}
    </div>
  );
}
